'use client';

import { useEffect } from 'react';
import Lenis from 'lenis';

/**
 * Genie-style inertia smooth scroll, mounted once at the app root. Honors
 * prefers-reduced-motion (skips entirely so native scrolling is preserved) and
 * routes in-page anchor clicks through Lenis so they glide instead of jump.
 */
export function SmoothScroll() {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const lenis = new Lenis({
      // Smooth but snappy: a shorter settle and a full-strength wheel notch so
      // the page glides without feeling heavy or making you scroll forever.
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.5,
    });

    // Expose the instance so scroll-driven sections (GSAP ScrollTrigger) can sync
    // their pins to Lenis: `window.lenis?.on('scroll', ScrollTrigger.update)`.
    (window as unknown as { lenis?: Lenis }).lenis = lenis;

    let raf = 0;
    const loop = (time: number) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    // Same-page anchor links (#id) glide via Lenis.
    const onClick = (e: MouseEvent) => {
      const anchor = (e.target as HTMLElement)?.closest('a[href*="#"]') as HTMLAnchorElement | null;
      if (!anchor) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.pathname !== window.location.pathname || !url.hash) return;
      const target = document.querySelector(url.hash);
      if (!target) return;
      e.preventDefault();
      lenis.scrollTo(target as HTMLElement, { offset: -80 });
    };
    document.addEventListener('click', onClick);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('click', onClick);
      (window as unknown as { lenis?: Lenis }).lenis = undefined;
      lenis.destroy();
    };
  }, []);

  return null;
}
